// The feedback inbox (docs/plans/phase3-portal-feedback-kiswahili.md §5.3): weekly trends,
// the list (no comment text: that is read on the detail, which is audited), and the detail
// with reply, resolve and escalate. Escalating calls the incident hook; until incident
// reporting is set up the feedback is marked escalated with its reason.
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { feedbackRatingWord, formatEatDate } from '@wodi/domain';
import type { FeedbackDetailView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  actOnFeedback,
  feedbackDetailQuery,
  feedbackListQuery,
  feedbackTrendsQuery,
  portalStaffKeys,
} from '../api/portal-staff.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen } from '../layout/screen.tsx';
import { toasts } from '../toasts.ts';

const message = (error: unknown) =>
  error instanceof ApiError ? error.message : 'Something went wrong. Try again.';

const STATUS_OPTIONS = [
  { id: 'open', label: 'Open' },
  { id: 'escalated', label: 'Escalated' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'all', label: 'All' },
] as const;

const RATING_OPTIONS = [
  { id: 'all', label: 'Any rating' },
  { id: 'low', label: '1 or 2' },
  { id: 'middle', label: '3' },
  { id: 'high', label: '4 or 5' },
] as const;

const rating = (n: number) => `${String(n)}: ${feedbackRatingWord(n)}`;

function Trends() {
  const trends = useQuery(feedbackTrendsQuery);
  return (
    <Section title="Last 12 weeks">
      {trends.isPending ? <LoadingBlock label="Loading trends" rows={2} /> : null}
      {trends.isError ? <Notice tone="error">{message(trends.error)}</Notice> : null}
      {trends.data === undefined ? null : (
        <>
          <p>{`${String(trends.data.open)} open, ${String(trends.data.escalated)} of them escalated.`}</p>
          <Table aria-label="Feedback per week">
            <TableHeader>
              <Column isRowHeader>Week starting</Column>
              <Column>Ratings</Column>
              <Column>Average</Column>
              <Column>Rated 1 or 2</Column>
            </TableHeader>
            <TableBody>
              {[...trends.data.weeks].reverse().map((w) => (
                <Row key={w.weekStart}>
                  <Cell>{formatEatDate(new Date(`${w.weekStart}T09:00:00Z`))}</Cell>
                  <Cell>{String(w.count)}</Cell>
                  <Cell>{w.average === null ? 'None' : w.average.toFixed(1)}</Cell>
                  <Cell>{String(w.low)}</Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Section>
  );
}

function Detail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const detail = useQuery(feedbackDetailQuery(id));
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'reply' | 'resolve' | 'escalate' | null>(null);
  const act = useMutation({
    mutationFn: (input: { action: 'reply' | 'resolve' | 'escalate'; text: string }) =>
      actOnFeedback(id, input.action, input.text),
    onSuccess: async (saved: FeedbackDetailView, input) => {
      queryClient.setQueryData(feedbackDetailQuery(id).queryKey, saved);
      await queryClient.invalidateQueries({ queryKey: portalStaffKeys.feedback });
      setMode(null);
      setText('');
      toasts.show({
        title:
          input.action === 'reply'
            ? 'Reply sent'
            : input.action === 'resolve'
              ? 'Feedback resolved'
              : 'Feedback escalated',
      });
    },
  });
  const mayRespond = session !== null && can(session, 'feedback.respond');
  const mayEscalate = session !== null && can(session, 'feedback.escalate');
  const f = detail.data;
  return (
    <Section
      title="Feedback"
      actions={
        <Button dense onPress={onClose}>
          Close
        </Button>
      }
    >
      {detail.isPending ? <LoadingBlock label="Loading feedback" rows={3} /> : null}
      {detail.isError ? <Notice tone="error">{message(detail.error)}</Notice> : null}
      {f === undefined ? null : (
        <div className={screen.page}>
          <p>
            <strong>{rating(f.rating)}</strong>
            {` from ${f.patientName} (${f.patientNumber}), visit ${f.visitNumber}, ${formatEatDate(new Date(f.submittedAt))}, by ${f.source === 'portal' ? 'the portal' : 'SMS link'}.`}
          </p>
          <p>{f.comment ?? 'No comment.'}</p>
          {f.reply === null ? null : (
            <Notice tone="info">{`Reply, ${f.repliedBy?.displayName ?? ''}: ${f.reply}`}</Notice>
          )}
          {f.escalation === null ? null : (
            <Notice tone="info">
              {`Escalated by ${f.escalation.by.displayName}: ${f.escalation.reason}. `}
              {f.escalation.incidentId === null
                ? 'The incident will be linked when incident reporting is set up.'
                : 'Linked to an incident.'}
            </Notice>
          )}
          {f.status === 'resolved' ? (
            <p>{`Resolved by ${f.resolvedBy?.displayName ?? ''}: ${f.resolutionNote ?? ''}`}</p>
          ) : null}
          {act.error === null ? null : <Notice tone="error">{message(act.error)}</Notice>}
          {mode === null ? (
            <div>
              {mayRespond && f.reply === null ? (
                <Button
                  dense
                  isDisabled={!online}
                  onPress={() => {
                    setMode('reply');
                  }}
                >
                  Reply
                </Button>
              ) : null}{' '}
              {mayRespond && f.status === 'open' ? (
                <Button
                  dense
                  isDisabled={!online}
                  onPress={() => {
                    setMode('resolve');
                  }}
                >
                  Mark resolved
                </Button>
              ) : null}{' '}
              {mayEscalate && f.escalation === null ? (
                <Button
                  dense
                  isDisabled={!online}
                  onPress={() => {
                    setMode('escalate');
                  }}
                >
                  Escalate to an incident
                </Button>
              ) : null}
            </div>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                act.mutate({ action: mode, text });
              }}
            >
              <TextArea
                label={
                  mode === 'reply'
                    ? 'Reply the patient sees in the portal'
                    : mode === 'resolve'
                      ? 'What was done'
                      : 'Why it needs an incident'
                }
                rows={3}
                value={text}
                onChange={setText}
              />
              <div>
                <Button
                  dense
                  onPress={() => {
                    setMode(null);
                  }}
                >
                  Cancel
                </Button>{' '}
                <Button dense type="submit" variant="primary" isPending={act.isPending}>
                  {mode === 'reply'
                    ? 'Send reply'
                    : mode === 'resolve'
                      ? 'Mark resolved'
                      : 'Escalate'}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </Section>
  );
}

export function FeedbackInboxPage() {
  const online = useOnline();
  const [status, setStatus] = useState<string>('open');
  const [band, setBand] = useState<string>('all');
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery(feedbackListQuery(status, band));
  return (
    <div className={screen.page}>
      <PageHeader title="Feedback" description="What patients said about their visits here." />
      <Trends />
      {open === null ? null : (
        <Detail
          id={open}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      <Section title="Feedback">
        <SegmentedControl
          label="Show"
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
        <SegmentedControl label="Rating" options={RATING_OPTIONS} value={band} onChange={setBand} />
        {list.isPending ? <LoadingBlock label="Loading feedback" rows={3} /> : null}
        {list.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense isDisabled={!online} onPress={() => void list.refetch()}>
                Try again
              </Button>
            }
          >
            {online ? message(list.error) : "You're offline. Feedback loads once you reconnect."}
          </Notice>
        ) : null}
        {list.data?.items.length === 0 ? <EmptyState>No feedback here.</EmptyState> : null}
        {list.data === undefined || list.data.items.length === 0 ? null : (
          <Table aria-label="Feedback">
            <TableHeader>
              <Column isRowHeader>Received</Column>
              <Column>Rating</Column>
              <Column>Visit</Column>
              <Column>State</Column>
              <Column>Open</Column>
            </TableHeader>
            <TableBody>
              {list.data.items.map((f) => (
                <Row key={f.id}>
                  <Cell>{formatEatDate(new Date(f.submittedAt))}</Cell>
                  <Cell>{`${rating(f.rating)}${f.hasComment ? ', with a comment' : ''}`}</Cell>
                  <Cell>{f.visitNumber}</Cell>
                  <Cell>
                    <Tag
                      state={
                        f.status === 'resolved' ? 'done' : f.escalated ? 'attention' : 'pending'
                      }
                    >
                      {f.status === 'resolved' ? 'Resolved' : f.escalated ? 'Escalated' : 'Open'}
                    </Tag>
                  </Cell>
                  <Cell>
                    <Button
                      dense
                      onPress={() => {
                        setOpen(f.id);
                      }}
                    >
                      Read
                    </Button>
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </div>
  );
}
